/**
 * InteractionContext - React Context for managing cross-chart interaction state
 * 
 * Provides centralized state management for highlight and filter interactions
 * across all charts in a dashboard.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { InteractionBus, HighlightPayload, FilterPayload, ClearPayload } from '../services/interactionBus';

// State Types
export interface HighlightState {
    sourceChartId: string;
    field: string;
    values: any[];
}

export interface FilterState {
    sourceChartId: string;
    field: string;
    values: any[];
}

interface InteractionContextValue {
    // State
    highlightState: HighlightState | null;
    activeFilters: FilterState[];

    // Actions
    setHighlight: (payload: HighlightPayload) => void;
    clearHighlight: () => void;
    addFilter: (payload: FilterPayload) => void;
    removeFilter: (field: string) => void;
    clearAllFilters: () => void;

    // Utilities
    isDataPointHighlighted: (chartId: string, field: string, value: any) => boolean;
    isDataPointFiltered: (chartId: string, field: string, value: any) => boolean | null;
    getFilteredValues: (field: string) => any[] | null;
}

const InteractionContext = createContext<InteractionContextValue | null>(null);

interface InteractionProviderProps {
    children: ReactNode;
}

export const InteractionProvider: React.FC<InteractionProviderProps> = ({ children }) => {
    const [highlightState, setHighlightState] = useState<HighlightState | null>(null);
    const [activeFilters, setActiveFilters] = useState<FilterState[]>([]);

    // Subscribe to InteractionBus events
    useEffect(() => {
        const handleHighlight = (payload: HighlightPayload) => {
            setHighlightState({
                sourceChartId: payload.sourceChartId,
                field: payload.field,
                values: payload.values
            });
        };

        const handleFilter = (payload: FilterPayload) => {
            setActiveFilters(prev => {
                // Replace existing filter for the same field, or add new
                const existing = prev.findIndex(f => f.field === payload.field);
                if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = {
                        sourceChartId: payload.sourceChartId,
                        field: payload.field,
                        values: payload.values
                    };
                    return updated;
                }
                return [...prev, {
                    sourceChartId: payload.sourceChartId,
                    field: payload.field,
                    values: payload.values
                }];
            });
        };

        const handleClear = (payload: ClearPayload) => {
            if (payload.clearType === 'highlight' || payload.clearType === 'all') {
                setHighlightState(null);
            }
            if (payload.clearType === 'filter' || payload.clearType === 'all') {
                setActiveFilters([]);
            }
        };

        const unsubHighlight = InteractionBus.on('highlight', handleHighlight as any);
        const unsubFilter = InteractionBus.on('filter', handleFilter as any);
        const unsubClear = InteractionBus.on('clear', handleClear as any);

        return () => {
            unsubHighlight();
            unsubFilter();
            unsubClear();
        };
    }, []);

    // ESC key handler to clear all filters
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                InteractionBus.emit('clear', { clearType: 'all' });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Actions
    const setHighlight = useCallback((payload: HighlightPayload) => {
        InteractionBus.emit('highlight', payload);
    }, []);

    const clearHighlight = useCallback(() => {
        InteractionBus.emit('clear', { clearType: 'highlight' });
    }, []);

    const addFilter = useCallback((payload: FilterPayload) => {
        InteractionBus.emit('filter', payload);
    }, []);

    const removeFilter = useCallback((field: string) => {
        setActiveFilters(prev => prev.filter(f => f.field !== field));
    }, []);

    const clearAllFilters = useCallback(() => {
        InteractionBus.emit('clear', { clearType: 'all' });
    }, []);

    // Utilities
    const isDataPointHighlighted = useCallback((chartId: string, field: string, value: any): boolean => {
        if (!highlightState) return true; // No highlight = all highlighted
        if (highlightState.sourceChartId === chartId) return true; // Source chart is always fully visible
        if (highlightState.field !== field) return true; // Different field = no dimming
        return highlightState.values.includes(value);
    }, [highlightState]);

    const isDataPointFiltered = useCallback((chartId: string, field: string, value: any): boolean | null => {
        const filter = activeFilters.find(f => f.field === field);
        if (!filter) return null; // No filter for this field
        if (filter.sourceChartId === chartId) return null; // Source chart not filtered
        return filter.values.includes(value);
    }, [activeFilters]);

    const getFilteredValues = useCallback((field: string): any[] | null => {
        const filter = activeFilters.find(f => f.field === field);
        return filter?.values || null;
    }, [activeFilters]);

    const contextValue = useMemo(() => ({
        highlightState,
        activeFilters,
        setHighlight,
        clearHighlight,
        addFilter,
        removeFilter,
        clearAllFilters,
        isDataPointHighlighted,
        isDataPointFiltered,
        getFilteredValues
    }), [
        highlightState,
        activeFilters,
        setHighlight,
        clearHighlight,
        addFilter,
        removeFilter,
        clearAllFilters,
        isDataPointHighlighted,
        isDataPointFiltered,
        getFilteredValues
    ]);

    return (
        <InteractionContext.Provider value={contextValue}>
            {children}
        </InteractionContext.Provider>
    );
};

export const useInteraction = (): InteractionContextValue => {
    const context = useContext(InteractionContext);
    if (!context) {
        throw new Error('useInteraction must be used within an InteractionProvider');
    }
    return context;
};

// Optional hook for components that may or may not be in an InteractionProvider
export const useInteractionOptional = (): InteractionContextValue | null => {
    return useContext(InteractionContext);
};
